import { tokens } from '@neo4j-ndl/base';
import type { NeoNode, NeoRel } from '@neo4j-ndl/react';
import { DbmsIcon, ServerIconOutline } from '@neo4j-ndl/react/icons';
import type { ProjectMember } from '@nx/state';
import { FleetManagementTypes } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import type { JSX } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { ExploreIconOutline } from '../../../../upx/src/components/icons';
import { timePeriodFormatter } from '../../shared/ui-helpers';
import { tokenExpirySoon, tokenExpiryText } from '../deployments/token-management';
import { hasUnmonitoredServers } from '../shared/components/server-status';

export const colors = {
  server: tokens.colors.baltic[35],
  systemDatabase: tokens.colors.neutral[35],
  database: tokens.colors.earth[35],
  online: tokens.colors.forest[35],
  offline: tokens.colors.hibiscus[35],
  degraded: tokens.colors.marigold[35],
};

const HtmlElementWithIcon = (elem: JSX.Element) => {
  const divWithIcon = document.createElement('div');
  divWithIcon.style.display = 'flex';
  divWithIcon.style.justifyContent = 'center';
  divWithIcon.style.alignItems = 'flex-end';
  divWithIcon.style.height = '44px';
  divWithIcon.style.width = '48px';
  divWithIcon.innerHTML = renderToStaticMarkup(elem);

  return divWithIcon;
};

const getTokenWarningText = (token: FleetManagementTypes.Token | undefined): string | undefined => {
  if (!token || !tokenExpirySoon(token)) {
    return undefined;
  }

  return tokenExpiryText(token);
};

const getLicenseWarningText = (d: FleetManagementTypes.Dbms | undefined): string | undefined => {
  if (isNotNullish(d) && FleetManagementTypes.hasLicenseIssues(d)) {
    return `License issues detected. Check the Inspect pane for more information.`;
  }

  return undefined;
};

export const getDbmsNode = (
  d: FleetManagementTypes.DeploymentDetails,
  getProjectMembersRes: ProjectMember[] | undefined,
): NeoNode => {
  const creator = getProjectMembersRes?.find((member) => member.userId === d.token?.createdBy)?.email;

  const hasUnmonitored = hasUnmonitoredServers(d);
  const tokenWarning = getTokenWarningText(d.token);
  const licenseWarning = getLicenseWarningText(d.dbms);
  let color;
  let label;
  switch (d.status) {
    case FleetManagementTypes.Api.DEPLOYMENT_STATUS.DEGRADED:
      color = colors.degraded;
      label = 'Degraded';
      break;
    case FleetManagementTypes.Api.DEPLOYMENT_STATUS.RUNNING:
      color = colors.online;
      label = 'Online';
      break;
    default:
      color = colors.offline;
      label = 'Offline';
  }

  const unmonitoredText = hasUnmonitored
    ? 'Deployment has unmonitored servers. Consider installing fleet management plugin on every server.'
    : undefined;

  return {
    labels: [label, 'DBMS'],
    id: d.deploymentId,
    captionAlign: 'top',
    color: color,
    properties: {
      name: { stringified: d.name, type: 'String' },
      status: { stringified: d.status, type: 'String' },
      version: { stringified: d.dbms?.version ?? 'N/A', type: 'String' },
      edition: { stringified: d.dbms?.edition ?? 'N/A', type: 'String' },
      packaging: { stringified: d.dbms?.packaging ?? 'N/A', type: 'String' },
      'registered by': { stringified: creator ?? 'unknown user', type: 'String' },
      warnings: {
        stringified: [unmonitoredText, tokenWarning, licenseWarning].filter((s) => isNotNullish(s)).join(' '),
        type: 'String',
      },
    },
    html: HtmlElementWithIcon(<DbmsIcon className="h-4" />),
  };
};

export const getServerNode = (s: FleetManagementTypes.Server): NeoNode => {
  return {
    id: s.serverId,
    labels: ['Server'],
    captionAlign: 'top',
    color: colors.server,
    properties: {
      id: { stringified: s.serverId, type: 'String' },
      name: { stringified: s.displayName, type: 'String' },
      address: { stringified: s.address, type: 'String' },
      status: { stringified: s.status, type: 'String' },
      health: { stringified: s.health, type: 'String' },
      'mode constraint': { stringified: s.address, type: 'String' },
      version: { stringified: s.version ?? '', type: 'String' },
      'last ping': { stringified: timePeriodFormatter(new Date(s.lastPing)), type: 'String' },
    },
    html: HtmlElementWithIcon(<ServerIconOutline className="h-4" />),
  };
};

export const getDatabaseNode = (d: FleetManagementTypes.Database, dbmsId: string): NeoNode => {
  return {
    id: `db${d.databaseId}${d.name}${dbmsId}`,
    labels: [d.name === 'system' ? 'SystemDatabase' : 'Database'],
    captionAlign: 'top',
    color: d.name === 'system' ? colors.systemDatabase : colors.database,
    properties: { name: { stringified: d.name, type: 'String' } },
    html: HtmlElementWithIcon(<ExploreIconOutline className="h-4" />),
  };
};

export const getDbmsToServerRelationship = (
  deployment: FleetManagementTypes.DeploymentDetails,
  s: FleetManagementTypes.Server,
): NeoRel => {
  return {
    id: `${deployment.deploymentId}-${s.serverId}`,
    from: deployment.deploymentId,
    to: s.serverId,
    captions: [{ value: 'COMPRISES' }],
    type: 'COMPRISES',
    properties: {},
  };
};

export const getDbmsToDatabaseRelationship = (
  deployment: FleetManagementTypes.DeploymentDetails,
  d: FleetManagementTypes.Database,
): NeoRel => {
  return {
    id: `${deployment.deploymentId}-${d.databaseId}${d.name}`,
    from: deployment.deploymentId,
    to: `db${d.databaseId}${d.name}${deployment.deploymentId}`,
    captions: [{ value: 'HAS_DATABASE' }],
    type: 'HAS_DATABASE',
    properties: {},
  };
};

export const getServerToDatabaseRelationship = (di: FleetManagementTypes.DatabaseInstance, dbmsId: string): NeoRel => {
  return {
    id: `${di.serverId}-db${di.databaseId}${di.databaseName}${dbmsId}`,
    from: di.serverId,
    to: `db${di.databaseId}${di.databaseName}${dbmsId}`,
    captions: [{ value: `${di.role.toUpperCase()}_FOR` }],
    type: `${di.role.toUpperCase()}_FOR`,
    properties: {
      role: { stringified: di.role, type: 'String' },
      access: { stringified: di.access, type: 'String' },
      requestedStatus: { stringified: di.requestedStatus, type: 'String' },
      currentStatus: { stringified: di.currentStatus, type: 'String' },
      lastCommittedTxn: {
        stringified: isNotNullish(di.lastCommittedTxn) ? String(di.lastCommittedTxn) : '',
        type: 'Number',
      },
      replicationLag: { stringified: isNotNullish(di.replicationLag) ? String(di.replicationLag) : '', type: 'Number' },
      aliases: { stringified: di.aliases.join(','), type: 'String' },
      default: { stringified: di.default ? 'true' : 'false', type: 'Boolean' },
      lastStartTime: { stringified: timePeriodFormatter(new Date(di.lastStartTime)), type: 'Date' },
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      store: { stringified: di.store ?? '', type: 'String' },
      writer: { stringified: di.writer ? 'true' : 'false', type: 'Boolean' },
    },
  };
};

export const addServerNode = (
  s: FleetManagementTypes.Server,
  newNodes: NeoNode[],
  newRels: NeoRel[],
  deployment: FleetManagementTypes.DeploymentDetails,
) => {
  newNodes.push(getServerNode(s));
  newRels.push(getDbmsToServerRelationship(deployment, s));
};

export const addDatabaseNode = (
  newNodes: NeoNode[],
  d: FleetManagementTypes.Database,
  newRels: NeoRel[],
  deployment: FleetManagementTypes.DeploymentDetails,
) => {
  newNodes.push(getDatabaseNode(d, deployment.deploymentId));
  newRels.push(getDbmsToDatabaseRelationship(deployment, d));
};
